import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    AlertCircle,
    ChevronDown,
    ChevronRight,
    ExternalLink,
    FileText,
    Loader2,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import { NavigationGuard } from '../../ui/navigation-guard';
import { EVENT_KEYS, trackClick } from '../../../services/api/trackingService';
import { useToast } from '../../../hooks/use-toast';
import { cn } from '../../../lib/utils';
import { DocumentSchema } from '../../documents/types';
import {
    createEvaluateCondition,
    validateRequiredFields,
} from '../../documents/utils/formUtils';
import {
    CaseReport,
    CaseStatus,
    getReport,
    getReportingFormData,
    saveReport,
} from '../../../services/reportService';
import { EditLog } from '../../documents/EditLog';
import { AutoSaveIndicator } from '../../documents/AutoSaveIndicator';
import { useAutoSave } from '../../../hooks/useAutoSave';
import CaseDocumentEditor from './CaseDocumentEditor';
import CaseDocumentReadView from './CaseDocumentReadView';
import { EditCompletedReportDialog } from './EditCompletedReportDialog';
import { CaseSaveCommentDialog } from './CaseSaveCommentDialog';
import CaseWorkspaceFooter from './CaseWorkspaceFooter';
import { CptReportPreview } from './CptReportPreview';
import { PreviewRenderDocument } from './utils/previewMergeUtils';
import {
    buildCaseBaseDefaults,
    CASE_CONTEXT_SCHEMA_KEY,
    CASE_DOCUMENT_SCHEMA_KEYS,
    CaseDocumentSchemaKey,
    createRiskBenefitDefaultsFromInitialEvaluationData,
    DEVICE_PREPARATION_SCHEMA_KEY,
    getDocumentBySchemaKey,
    getCaseContextValuesFromData,
    getInitialEvaluationFeasibilityValue,
    getInitialEvaluationSummaryFromData,
    INITIAL_EVALUATION_SCHEMA_KEY,
    POSITIONING_IMMOBILIZATION_SCHEMA_KEY,
    RISK_BENEFIT_SCHEMA_KEY,
    WorkspaceDocumentMode,
} from './utils/caseWorkspaceUtils';
import {
    build76016StatusList,
    build76018StatusList,
    build76019StatusList,
    resolveApplicableCptCodes,
} from './utils/cptEligibility';
import {
    DEVICE_PREPARATION_ONLY_SCENARIO,
    getWorkspaceScenarioConfig,
    RISK_BENEFIT_ONLY_SCENARIO,
    resolveWorkspaceScenarioKey,
    WorkspaceDocumentFlow,
} from './utils/workspaceConfig';
import {
    ACTIVE_IMPLANT_REF_KEY,
    ActiveImplantRef,
    buildActiveImplantBacklinkPath,
    buildDevicePreparationDefaultsFromReportingForm,
    buildInitialEvaluationDefaultsFromReportingForm,
} from './utils/reportingFormSeedUtils';
import {
    resolveWorkspaceSchemas,
    SchemaPayload,
} from './utils/workspaceSchemaUtils';

const pageShellClassName = 'container mx-auto max-w-6xl space-y-6 px-4 py-6';

const resolveSchemaCardClassName = (
    schema?: Pick<DocumentSchema, 'style'> | null
) =>
    cn(
        schema?.style?.noBorder && 'border-none shadow-none',
        schema?.style?.noBackground && 'bg-transparent',
        schema?.style?.className
    );

const isSupportedCaseDocumentSchemaKey = (
    value: string | null
): value is CaseDocumentSchemaKey => {
    if (!value) return false;
    return CASE_DOCUMENT_SCHEMA_KEYS.includes(
        value as (typeof CASE_DOCUMENT_SCHEMA_KEYS)[number]
    );
};

const resolveCaseIdFromFormData = (formData: any): string =>
    formData?.user?.caseId || formData?.user?.caseToken || '';

const getNestedValue = (value: any, path: string): any => {
    if (!path) return undefined;
    return path.split('.').reduce((acc, part) => {
        if (acc === undefined || acc === null) return undefined;
        return acc[part];
    }, value);
};

const evaluateExpressionWithContext = (
    expression: string | undefined,
    data: Record<string, any>
): boolean => {
    if (!expression) return true;

    const watch = ((key?: string) => {
        if (!key) return data;
        return data[key];
    }) as any;

    const evaluate = createEvaluateCondition(watch, data);
    return evaluate(expression);
};

const hasMeaningfulDraftData = (value?: Record<string, any>): boolean => {
    if (!value) return false;
    return Object.keys(value).length > 0;
};

const mergeDocumentData = (
    ...sources: Array<Record<string, any> | null | undefined>
): Record<string, any> => {
    return sources.reduce<Record<string, any>>((mergedData, source) => {
        if (!source || typeof source !== 'object' || Array.isArray(source)) {
            return mergedData;
        }

        return {
            ...mergedData,
            ...source,
        };
    }, {});
};

const buildPanelModes = (
    flows: WorkspaceDocumentFlow[]
): Partial<Record<CaseDocumentSchemaKey, WorkspaceDocumentMode>> => {
    const modes: Partial<Record<CaseDocumentSchemaKey, WorkspaceDocumentMode>> =
        {};

    flows.forEach((flow) => {
        modes[flow.schemaKey] = 'view';
    });

    return modes;
};

const buildExpandedPanels = (
    flows: WorkspaceDocumentFlow[]
): Partial<Record<CaseDocumentSchemaKey, boolean>> => {
    const expanded: Partial<Record<CaseDocumentSchemaKey, boolean>> = {};

    flows.forEach((flow) => {
        expanded[flow.schemaKey] = true;
    });

    return expanded;
};

const CaseWorkspacePage: React.FC = () => {
    const { caseId } = useParams<{ caseId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const { toast } = useToast();

    const isNewCaseMode = !caseId;
    const scenarioKey = resolveWorkspaceScenarioKey(
        searchParams.get('scenario'),
        searchParams.get('workspaceMode')
    );
    const scenarioConfig = getWorkspaceScenarioConfig(scenarioKey);
    const requestedSchemaKey = searchParams.get('schemaKey');
    const lookupId = searchParams.get('implant');
    // For active systems `activeConfig` holds comma-joined config ids; it is
    // sent to the server as `activeConfigIds` alongside `activeSystem`.
    const activeConfigId = searchParams.get('activeConfig');
    const activeSystemId = searchParams.get('activeSystem');

    const [expandedPanels, setExpandedPanels] = useState<
        Partial<Record<CaseDocumentSchemaKey, boolean>>
    >(() => buildExpandedPanels(scenarioConfig.documentFlows));
    const [panelModes, setPanelModes] = useState<
        Partial<Record<CaseDocumentSchemaKey, WorkspaceDocumentMode>>
    >(() => buildPanelModes(scenarioConfig.documentFlows));

    const [caseData, setCaseData] = useState<CaseReport | null>(null);
    const [schemas, setSchemas] = useState<Record<string, SchemaPayload>>({});
    const [draftDocumentDataBySchema, setDraftDocumentDataBySchema] = useState<
        Partial<Record<CaseDocumentSchemaKey, Record<string, any>>>
    >({});
    const [dataSources, setDataSources] = useState<any>({
        scanners: [],
        policies: [],
        previousExams: [],
        previousTasks: [],
        previousPurposeOfImmobilization: [],
        departmentUsers: [],
        previousDeviceTypes: [],
        previousPrepActions: [],
        previousPrepMethods: [],
        previousPrepVerifications: [],
    });
    const [formData, setFormData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [workspaceError, setWorkspaceError] = useState<string | null>(null);
    const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
    const [isCompletingWorkspace, setIsCompletingWorkspace] = useState(false);
    const [isAutoSavingWorkspace, setIsAutoSavingWorkspace] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [hasRequestedRiskBenefit, setHasRequestedRiskBenefit] =
        useState(false);
    const [
        hasRequestedPositioningImmobilization,
        setHasRequestedPositioningImmobilization,
    ] = useState(false);
    const [hasRequestedDevicePreparation, setHasRequestedDevicePreparation] =
        useState(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [pendingEditPanelKey, setPendingEditPanelKey] =
        useState<CaseDocumentSchemaKey | null>(null);
    const [saveCommentDialog, setSaveCommentDialog] = useState<{
        open: boolean;
        targetStatus: CaseStatus;
    }>({ open: false, targetStatus: 'draft' });
    const skipAutoSaveAsBaselineRef = useRef(false);
    const hasInitializedFeasibilityRef = useRef(false);
    const previousFeasibilityRef = useRef<string>('');
    const hasShownRiskBenefitToastRef = useRef(false);

    useEffect(() => {
        setExpandedPanels(buildExpandedPanels(scenarioConfig.documentFlows));
        setPanelModes(buildPanelModes(scenarioConfig.documentFlows));
    }, [caseId, scenarioConfig]);

    useEffect(() => {
        setHasRequestedRiskBenefit(scenarioKey === RISK_BENEFIT_ONLY_SCENARIO);
        setHasRequestedDevicePreparation(
            scenarioKey === DEVICE_PREPARATION_ONLY_SCENARIO
        );
        hasInitializedFeasibilityRef.current = false;
        previousFeasibilityRef.current = '';
        hasShownRiskBenefitToastRef.current = false;
        setHasUserEdited(false);
    }, [caseId, scenarioKey]);

    // Watch for user-initiated pointer or keyboard interactions to detect
    // edits. We use document-level capture-phase listeners (rather than props
    // on a wrapper div) so events from Radix Select/Popover items — which
    // render in portals outside the workspace tree — are still captured, and
    // capture phase ensures we still see the event even if a child component
    // calls stopPropagation().
    useEffect(() => {
        const findInteractedPanelKey = (
            target: EventTarget | null
        ): CaseDocumentSchemaKey | null => {
            if (!(target instanceof Element)) return null;
            const panelEl = target.closest(
                '[data-panel-key]'
            ) as HTMLElement | null;
            const key = panelEl?.dataset.panelKey;
            if (!key) return null;
            return isSupportedCaseDocumentSchemaKey(key) ? key : null;
        };

        const onInteract = (event: Event) => {
            const panelKey = findInteractedPanelKey(event.target);
            if (!panelKey) return;
            setHasUserEdited(true);
            // Lock the interacted panel into edit mode so that auto-save
            // (which creates panelDocuments) does not flip it to ReadView
            // mid-edit. Only the panel the user actually interacted with is
            // affected — other panels keep their current mode.
            setPanelModes((prev) => {
                if (prev[panelKey] === 'edit') return prev;
                return { ...prev, [panelKey]: 'edit' };
            });
        };
        document.addEventListener('pointerdown', onInteract, true);
        document.addEventListener('keydown', onInteract, true);
        return () => {
            document.removeEventListener('pointerdown', onInteract, true);
            document.removeEventListener('keydown', onInteract, true);
        };
    }, []);

    useEffect(() => {
        if (!isSupportedCaseDocumentSchemaKey(requestedSchemaKey)) return;

        setExpandedPanels((prev) => ({
            ...prev,
            [requestedSchemaKey]: true,
        }));

        if (requestedSchemaKey === RISK_BENEFIT_SCHEMA_KEY) {
            setHasRequestedRiskBenefit(true);
        }
    }, [requestedSchemaKey]);

    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (next.get('scenario') !== scenarioKey) {
                next.set('scenario', scenarioKey);
            }
            next.delete('workspaceMode');
            return next;
        });
    }, [scenarioKey, setSearchParams]);

    useEffect(() => {
        const loadWorkspace = async () => {
            setIsLoading(true);
            setError(null);
            setWorkspaceError(null);

            try {
                const schemaKeys = [
                    scenarioConfig.footerSchemaKey,
                    ...scenarioConfig.documentFlows.map(
                        (flow) => flow.schemaKey
                    ),
                ];
                const uniqueSchemaKeys = Array.from(new Set(schemaKeys));

                if (caseId) {
                    const [reportCase, reportingFormData] = await Promise.all(
                        [
                            getReport(caseId),
                            getReportingFormData(
                                lookupId || undefined,
                                activeSystemId
                                    ? undefined
                                    : activeConfigId || undefined,
                                activeSystemId || undefined,
                                activeSystemId
                                    ? activeConfigId || undefined
                                    : undefined
                            ),
                        ]
                    );

                    setCaseData(reportCase);
                    setFormData(reportingFormData);
                    setDataSources({
                        scanners: reportingFormData.department?.scanners || [],
                        policies: reportingFormData.department?.policies || [],
                        previousExams:
                            reportingFormData.department?.previousExams || [],
                        previousTasks:
                            reportingFormData.department?.previousTasks || [],
                        previousPurposeOfImmobilization:
                            reportingFormData.department
                                ?.previousPurposeOfImmobilization || [],
                        departmentUsers:
                            reportingFormData.department?.departmentUsers || [],
                        previousDeviceTypes:
                            reportingFormData.department?.previousDeviceTypes ||
                            [],
                        previousPrepActions:
                            reportingFormData.department?.previousPrepActions ||
                            [],
                        previousPrepMethods:
                            reportingFormData.department?.previousPrepMethods ||
                            [],
                        previousPrepVerifications:
                            reportingFormData.department
                                ?.previousPrepVerifications || [],
                    });

                    // Existing documents render with the schema version they
                    // were saved under; panels without a document get latest.
                    setSchemas(
                        await resolveWorkspaceSchemas(
                            uniqueSchemaKeys,
                            reportCase
                        )
                    );
                    return;
                }

                const [reportingFormData, nextSchemas] = await Promise.all([
                    getReportingFormData(
                        lookupId || undefined,
                        activeSystemId
                            ? undefined
                            : activeConfigId || undefined,
                        activeSystemId || undefined,
                        activeSystemId
                            ? activeConfigId || undefined
                            : undefined
                    ),
                    resolveWorkspaceSchemas(uniqueSchemaKeys, null),
                ]);

                setCaseData(null);
                setFormData(reportingFormData);
                setDataSources({
                    scanners: reportingFormData.department?.scanners || [],
                    policies: reportingFormData.department?.policies || [],
                    previousExams:
                        reportingFormData.department?.previousExams || [],
                    previousTasks:
                        reportingFormData.department?.previousTasks || [],
                    previousPurposeOfImmobilization:
                        reportingFormData.department
                            ?.previousPurposeOfImmobilization || [],
                    departmentUsers:
                        reportingFormData.department?.departmentUsers || [],
                    previousDeviceTypes:
                        reportingFormData.department?.previousDeviceTypes || [],
                    previousPrepActions:
                        reportingFormData.department?.previousPrepActions || [],
                    previousPrepMethods:
                        reportingFormData.department?.previousPrepMethods || [],
                    previousPrepVerifications:
                        reportingFormData.department
                            ?.previousPrepVerifications || [],
                });

                setSchemas(nextSchemas);
            } catch (workspaceLoadError: any) {
                const message =
                    workspaceLoadError?.response?.data?.message ||
                    workspaceLoadError?.message ||
                    'Failed to load case workspace.';
                setError(message);
            } finally {
                setIsLoading(false);
            }
        };

        loadWorkspace();
    }, [activeConfigId, activeSystemId, caseId, lookupId, scenarioConfig]);

    const isLegacyCase = useMemo(() => {
        if (!caseData) return false;
        if (!caseData.status) return true;
        const hasCaseContextDocument = !!getDocumentBySchemaKey(
            caseData,
            CASE_CONTEXT_SCHEMA_KEY
        );
        if (!hasCaseContextDocument) return true;
        return false;
    }, [caseData]);

    const footerSchema =
        schemas[scenarioConfig.footerSchemaKey]?.schema || null;
    const isCompletedCase = caseData?.status === 'completed';
    const canEditCaseDocuments =
        !caseData ||
        caseData.status === 'draft' ||
        caseData.status === 'auto-saved' ||
        isCompletedCase;

    const initialDocument = useMemo(
        () => getDocumentBySchemaKey(caseData, INITIAL_EVALUATION_SCHEMA_KEY),
        [caseData]
    );
    const riskBenefitDocument = useMemo(
        () => getDocumentBySchemaKey(caseData, RISK_BENEFIT_SCHEMA_KEY),
        [caseData]
    );
    const hasRiskBenefitDraft = hasMeaningfulDraftData(
        draftDocumentDataBySchema[RISK_BENEFIT_SCHEMA_KEY]
    );
    const positioningImmobilizationDocument = useMemo(
        () =>
            getDocumentBySchemaKey(
                caseData,
                POSITIONING_IMMOBILIZATION_SCHEMA_KEY
            ),
        [caseData]
    );
    const hasPositioningImmobilizationDraft = hasMeaningfulDraftData(
        draftDocumentDataBySchema[POSITIONING_IMMOBILIZATION_SCHEMA_KEY]
    );
    const devicePreparationDocument = useMemo(
        () => getDocumentBySchemaKey(caseData, DEVICE_PREPARATION_SCHEMA_KEY),
        [caseData]
    );
    const hasDevicePreparationDraft = hasMeaningfulDraftData(
        draftDocumentDataBySchema[DEVICE_PREPARATION_SCHEMA_KEY]
    );
    const baseDocumentDefaults = useMemo(
        () =>
            caseData
                ? buildCaseBaseDefaults(caseData, formData?.user?.name)
                : {
                      techName: formData?.user?.name || '',
                  },
        [caseData, formData?.user?.name]
    );
    const seededInitialEvaluationData = useMemo(
        () =>
            buildInitialEvaluationDefaultsFromReportingForm(
                baseDocumentDefaults,
                formData
            ),
        [baseDocumentDefaults, formData]
    );
    const hasSeededInitialEvaluationData = hasMeaningfulDraftData(
        seededInitialEvaluationData
    );
    const seededDevicePreparationData = useMemo(
        () =>
            buildDevicePreparationDefaultsFromReportingForm(
                baseDocumentDefaults,
                formData
            ),
        [baseDocumentDefaults, formData]
    );

    const sourceDataBySchema = useMemo(() => {
        return scenarioConfig.documentFlows.reduce((acc, flow) => {
            const seededSourceData =
                flow.schemaKey === INITIAL_EVALUATION_SCHEMA_KEY
                    ? seededInitialEvaluationData
                    : flow.schemaKey === DEVICE_PREPARATION_SCHEMA_KEY
                    ? seededDevicePreparationData
                    : {};
            acc[flow.schemaKey] = mergeDocumentData(
                seededSourceData,
                getDocumentBySchemaKey(caseData, flow.schemaKey)?.data,
                draftDocumentDataBySchema[flow.schemaKey]
            );
            return acc;
        }, {} as Partial<Record<CaseDocumentSchemaKey, Record<string, any>>>);
    }, [
        caseData,
        draftDocumentDataBySchema,
        scenarioConfig.documentFlows,
        seededDevicePreparationData,
        seededInitialEvaluationData,
    ]);

    // Backlink to the active implant configuration this case was created
    // from (persisted as _activeImplantRef inside document data).
    const activeImplantRef = useMemo<ActiveImplantRef | null>(() => {
        for (const flow of scenarioConfig.documentFlows) {
            const ref =
                sourceDataBySchema[flow.schemaKey]?.[ACTIVE_IMPLANT_REF_KEY];
            if (ref && typeof ref === 'object' && ref.systemId) {
                return ref as ActiveImplantRef;
            }
        }
        return null;
    }, [scenarioConfig.documentFlows, sourceDataBySchema]);

    const effectiveCaseContextValues = useMemo(() => {
        const caseContextSourceData =
            sourceDataBySchema[CASE_CONTEXT_SCHEMA_KEY] || {};
        return getCaseContextValuesFromData(
            caseContextSourceData,
            caseData?.caseId || resolveCaseIdFromFormData(formData)
        );
    }, [caseData?.caseId, formData, sourceDataBySchema]);
    const currentInitialEvaluationData = useMemo(
        () => sourceDataBySchema[INITIAL_EVALUATION_SCHEMA_KEY] || {},
        [sourceDataBySchema]
    );
    const effectiveApplicableCodes = useMemo(
        () =>
            resolveApplicableCptCodes({
                initialEvaluationData:
                    sourceDataBySchema[INITIAL_EVALUATION_SCHEMA_KEY] ||
                    initialDocument?.data,
                riskBenefitData:
                    sourceDataBySchema[RISK_BENEFIT_SCHEMA_KEY] ||
                    riskBenefitDocument?.data,
                positioningImmobilizationData:
                    sourceDataBySchema[POSITIONING_IMMOBILIZATION_SCHEMA_KEY] ||
                    positioningImmobilizationDocument?.data,
                devicePreparationData:
                    sourceDataBySchema[DEVICE_PREPARATION_SCHEMA_KEY] ||
                    devicePreparationDocument?.data,
                savedApplicableCodes: caseData?.cptEligibleCodes,
            }),
        [
            caseData?.cptEligibleCodes,
            devicePreparationDocument?.data,
            initialDocument?.data,
            positioningImmobilizationDocument?.data,
            riskBenefitDocument?.data,
            sourceDataBySchema,
        ]
    );
    const isRiskBenefitRequested =
        hasRequestedRiskBenefit ||
        scenarioKey === RISK_BENEFIT_ONLY_SCENARIO ||
        requestedSchemaKey === RISK_BENEFIT_SCHEMA_KEY ||
        !!riskBenefitDocument ||
        hasRiskBenefitDraft;
    const isPositioningImmobilizationRequested =
        hasRequestedPositioningImmobilization ||
        requestedSchemaKey === POSITIONING_IMMOBILIZATION_SCHEMA_KEY ||
        !!positioningImmobilizationDocument ||
        hasPositioningImmobilizationDraft;
    const isDevicePreparationRequested =
        hasRequestedDevicePreparation ||
        scenarioKey === DEVICE_PREPARATION_ONLY_SCENARIO ||
        requestedSchemaKey === DEVICE_PREPARATION_SCHEMA_KEY ||
        !!devicePreparationDocument ||
        hasDevicePreparationDraft;

    const sharedRuntimeCaseContext = useMemo(
        () => ({
            caseId: effectiveCaseContextValues.caseId,
            exam: effectiveCaseContextValues.exam,
            clinicalIndication: effectiveCaseContextValues.clinicalIndication,
            scannerId: effectiveCaseContextValues.scannerId,
            scanDate: effectiveCaseContextValues.scanDate,
            assessmentDate: effectiveCaseContextValues.scanDate,
            // Attestation signature requests: the attestation components and
            // lock-to-signer logic resolve these for every schema.
            currentUserId: formData?.user?._id || '',
            attestationName: formData?.user?.name || '',
            departmentUsers: dataSources?.departmentUsers || [],
        }),
        [
            dataSources?.departmentUsers,
            effectiveCaseContextValues,
            formData?.user?._id,
            formData?.user?.name,
        ]
    );

    const runtimeContextBySchema = useMemo(() => {
        const contextMap: Partial<
            Record<CaseDocumentSchemaKey, Record<string, any>>
        > = {};

        scenarioConfig.documentFlows.forEach((flow) => {
            const schema = schemas[flow.schemaKey]?.schema;
            const panelSourceData = sourceDataBySchema[flow.schemaKey] || {};

            if (!schema) {
                contextMap[flow.schemaKey] = {
                    ...sharedRuntimeCaseContext,
                    ...panelSourceData,
                };
                return;
            }

            const importedContext = (schema.contextImports || []).reduce(
                (acc, importRule) => {
                    const sourceSchemaKey =
                        importRule.sourceSchemaKey as CaseDocumentSchemaKey;
                    const sourceData =
                        sourceDataBySchema[sourceSchemaKey] || {};
                    const importedValue = getNestedValue(
                        sourceData,
                        importRule.sourceField
                    );
                    acc[importRule.id] =
                        importedValue !== undefined
                            ? importedValue
                            : importRule.fallback;
                    return acc;
                },
                {} as Record<string, any>
            );

            contextMap[flow.schemaKey] = {
                ...sharedRuntimeCaseContext,
                ...panelSourceData,
                ...importedContext,
            };

            if (flow.schemaKey === RISK_BENEFIT_SCHEMA_KEY) {
                contextMap[flow.schemaKey] = {
                    ...contextMap[flow.schemaKey],
                    cpt76016StatusList: build76016StatusList(panelSourceData),
                };
            }

            if (flow.schemaKey === POSITIONING_IMMOBILIZATION_SCHEMA_KEY) {
                contextMap[flow.schemaKey] = {
                    ...contextMap[flow.schemaKey],
                    cpt76019StatusList: build76019StatusList(
                        panelSourceData,
                        importedContext
                    ),
                };
            }

            if (flow.schemaKey === DEVICE_PREPARATION_SCHEMA_KEY) {
                contextMap[flow.schemaKey] = {
                    ...contextMap[flow.schemaKey],
                    cpt76018StatusList: build76018StatusList(panelSourceData),
                };
            }
        });

        return contextMap;
    }, [scenarioConfig, schemas, sharedRuntimeCaseContext, sourceDataBySchema]);

    const dependencySatisfiedBySchema = useMemo(() => {
        const dependencyMap: Partial<Record<CaseDocumentSchemaKey, boolean>> =
            {};

        scenarioConfig.documentFlows.forEach((flow) => {
            const dependencySatisfied = (flow.dependsOn || []).every(
                (dependencyKey) => {
                    const hasPersistedDependency = !!getDocumentBySchemaKey(
                        caseData,
                        dependencyKey
                    );
                    const hasDraftDependency = hasMeaningfulDraftData(
                        draftDocumentDataBySchema[dependencyKey]
                    );
                    const hasSeededDependency =
                        dependencyKey === INITIAL_EVALUATION_SCHEMA_KEY &&
                        hasSeededInitialEvaluationData;

                    return (
                        hasPersistedDependency ||
                        hasDraftDependency ||
                        hasSeededDependency
                    );
                }
            );

            dependencyMap[flow.schemaKey] = dependencySatisfied;
        });

        return dependencyMap;
    }, [
        caseData,
        draftDocumentDataBySchema,
        hasSeededInitialEvaluationData,
        scenarioConfig,
    ]);

    const visibilityRuleSatisfiedBySchema = useMemo(() => {
        const visibilityMap: Partial<Record<CaseDocumentSchemaKey, boolean>> =
            {};

        scenarioConfig.documentFlows.forEach((flow) => {
            const schema = schemas[flow.schemaKey]?.schema;
            const visibilityWhen = schema?.workspace?.visibilityWhen;
            visibilityMap[flow.schemaKey] =
                !visibilityWhen ||
                flow.ignoreVisibilityWhen ||
                evaluateExpressionWithContext(
                    visibilityWhen,
                    runtimeContextBySchema[flow.schemaKey] || {}
                );
        });

        return visibilityMap;
    }, [runtimeContextBySchema, scenarioConfig, schemas]);

    const panelVisibility = useMemo(() => {
        const visibility: Partial<Record<CaseDocumentSchemaKey, boolean>> = {};

        scenarioConfig.documentFlows.forEach((flow) => {
            const dependencySatisfied =
                dependencySatisfiedBySchema[flow.schemaKey];
            const visibilityRuleSatisfied =
                visibilityRuleSatisfiedBySchema[flow.schemaKey];

            if (
                flow.schemaKey === RISK_BENEFIT_SCHEMA_KEY &&
                scenarioKey !== RISK_BENEFIT_ONLY_SCENARIO
            ) {
                visibility[flow.schemaKey] =
                    isRiskBenefitRequested &&
                    (hasRequestedRiskBenefit ||
                        !!riskBenefitDocument ||
                        (!!dependencySatisfied && !!visibilityRuleSatisfied));
                return;
            }

            if (flow.schemaKey === POSITIONING_IMMOBILIZATION_SCHEMA_KEY) {
                visibility[flow.schemaKey] =
                    isPositioningImmobilizationRequested &&
                    (hasRequestedPositioningImmobilization ||
                        !!positioningImmobilizationDocument ||
                        (!!dependencySatisfied && !!visibilityRuleSatisfied));
                return;
            }

            if (flow.schemaKey === DEVICE_PREPARATION_SCHEMA_KEY) {
                visibility[flow.schemaKey] =
                    isDevicePreparationRequested &&
                    (hasRequestedDevicePreparation ||
                        !!devicePreparationDocument ||
                        (!!dependencySatisfied && !!visibilityRuleSatisfied));
                return;
            }

            visibility[flow.schemaKey] =
                !!dependencySatisfied && !!visibilityRuleSatisfied;
        });

        return visibility;
    }, [
        dependencySatisfiedBySchema,
        devicePreparationDocument,
        hasRequestedDevicePreparation,
        hasRequestedPositioningImmobilization,
        hasRequestedRiskBenefit,
        isDevicePreparationRequested,
        isPositioningImmobilizationRequested,
        isRiskBenefitRequested,
        positioningImmobilizationDocument,
        riskBenefitDocument,
        scenarioConfig,
        scenarioKey,
        visibilityRuleSatisfiedBySchema,
    ]);

    const availableSchemaKeys = useMemo(() => {
        return scenarioConfig.documentFlows
            .map((flow) => flow.schemaKey)
            .filter((schemaKey) => panelVisibility[schemaKey]);
    }, [panelVisibility, scenarioConfig]);
    const includedSchemaKeys = useMemo(() => {
        return availableSchemaKeys.filter((schemaKey) => {
            if (schemaKey === CASE_CONTEXT_SCHEMA_KEY) {
                return true;
            }

            if (schemaKey === INITIAL_EVALUATION_SCHEMA_KEY) {
                return (
                    !!getDocumentBySchemaKey(caseData, schemaKey) ||
                    hasMeaningfulDraftData(
                        draftDocumentDataBySchema[schemaKey]
                    ) ||
                    hasSeededInitialEvaluationData
                );
            }

            if (schemaKey === RISK_BENEFIT_SCHEMA_KEY) {
                return (
                    !!getDocumentBySchemaKey(caseData, schemaKey) ||
                    hasMeaningfulDraftData(
                        draftDocumentDataBySchema[schemaKey]
                    ) ||
                    hasRequestedRiskBenefit
                );
            }

            if (schemaKey === POSITIONING_IMMOBILIZATION_SCHEMA_KEY) {
                return (
                    !!getDocumentBySchemaKey(caseData, schemaKey) ||
                    hasMeaningfulDraftData(
                        draftDocumentDataBySchema[schemaKey]
                    ) ||
                    hasRequestedPositioningImmobilization
                );
            }

            if (schemaKey === DEVICE_PREPARATION_SCHEMA_KEY) {
                return (
                    !!getDocumentBySchemaKey(caseData, schemaKey) ||
                    hasMeaningfulDraftData(
                        draftDocumentDataBySchema[schemaKey]
                    ) ||
                    hasRequestedDevicePreparation
                );
            }

            return true;
        });
    }, [
        availableSchemaKeys,
        caseData,
        draftDocumentDataBySchema,
        hasRequestedDevicePreparation,
        hasRequestedPositioningImmobilization,
        hasRequestedRiskBenefit,
        hasSeededInitialEvaluationData,
    ]);

    useEffect(() => {
        if (riskBenefitDocument || hasRiskBenefitDraft) {
            setHasRequestedRiskBenefit(true);
        }
    }, [hasRiskBenefitDraft, riskBenefitDocument]);

    useEffect(() => {
        if (
            positioningImmobilizationDocument ||
            hasPositioningImmobilizationDraft
        ) {
            setHasRequestedPositioningImmobilization(true);
        }
    }, [hasPositioningImmobilizationDraft, positioningImmobilizationDocument]);

    useEffect(() => {
        if (devicePreparationDocument || hasDevicePreparationDraft) {
            setHasRequestedDevicePreparation(true);
        }
    }, [devicePreparationDocument, hasDevicePreparationDraft]);

    const initialSummary = useMemo(
        () => getInitialEvaluationSummaryFromData(currentInitialEvaluationData),
        [currentInitialEvaluationData]
    );
    const currentFeasibilitySelection = useMemo(
        () =>
            getInitialEvaluationFeasibilityValue(currentInitialEvaluationData),
        [currentInitialEvaluationData]
    );
    const hasInitialEvaluationDocument = !!initialDocument;
    const hasInitialEvaluationContext =
        hasInitialEvaluationDocument ||
        hasMeaningfulDraftData(currentInitialEvaluationData);

    useEffect(() => {
        if (!hasInitializedFeasibilityRef.current) {
            previousFeasibilityRef.current = currentFeasibilitySelection;
            hasInitializedFeasibilityRef.current = true;
            return;
        }

        const previousFeasibility = previousFeasibilityRef.current;
        previousFeasibilityRef.current = currentFeasibilitySelection;

        if (
            previousFeasibility !== currentFeasibilitySelection &&
            !!currentFeasibilitySelection &&
            canEditCaseDocuments &&
            !isRiskBenefitRequested &&
            !hasShownRiskBenefitToastRef.current
        ) {
            hasShownRiskBenefitToastRef.current = true;
            toast({
                title: 'Risk-benefit analysis can be added',
                description:
                    'A physician/QHP risk-benefit analysis can be added to this case when you are ready.',
            });
        }
    }, [
        currentFeasibilitySelection,
        canEditCaseDocuments,
        isRiskBenefitRequested,
        toast,
    ]);

    const handlePanelDataChange = (
        panelKey: CaseDocumentSchemaKey,
        nextData: Record<string, any>
    ) => {
        setDraftDocumentDataBySchema((prev) => {
            // First emission for this panel = its seeded/initial data, not a
            // user edit. Flag the next useAutoSave effect run to treat the
            // resulting data change as a baseline rather than arming a save.
            if (!(panelKey in prev)) {
                skipAutoSaveAsBaselineRef.current = true;
            }
            return { ...prev, [panelKey]: nextData };
        });
    };

    const markUserEdited = useCallback(() => {
        setHasUserEdited(true);
    }, []);

    const togglePanel = (schemaKey: CaseDocumentSchemaKey) => {
        setExpandedPanels((prev) => ({
            ...prev,
            [schemaKey]: !prev[schemaKey],
        }));
    };

    const [completedEditConfirmed, setCompletedEditConfirmed] = useState(false);

    const commitPanelEdit = (schemaKey: CaseDocumentSchemaKey) => {
        setPanelModes((prev) => ({ ...prev, [schemaKey]: 'edit' }));
        setExpandedPanels((prev) => ({ ...prev, [schemaKey]: true }));
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('schemaKey', schemaKey);
            return next;
        });
    };

    const setPanelMode = (
        schemaKey: CaseDocumentSchemaKey,
        mode: WorkspaceDocumentMode
    ) => {
        if (!canEditCaseDocuments && mode === 'edit') {
            return;
        }

        if (mode === 'edit' && isCompletedCase && !completedEditConfirmed) {
            setPendingEditPanelKey(schemaKey);
            return;
        }

        if (mode === 'edit') {
            commitPanelEdit(schemaKey);
        } else {
            setPanelModes((prev) => ({ ...prev, [schemaKey]: mode }));
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (next.get('schemaKey') === schemaKey) {
                    next.delete('schemaKey');
                }
                return next;
            });
        }
    };

    const handleRequestRiskBenefit = () => {
        setHasRequestedRiskBenefit(true);
        setExpandedPanels((prev) => ({
            ...prev,
            [RISK_BENEFIT_SCHEMA_KEY]: true,
        }));
        setPanelModes((prev) => ({
            ...prev,
            [RISK_BENEFIT_SCHEMA_KEY]: 'edit',
        }));
        setDraftDocumentDataBySchema((prev) => {
            if (hasMeaningfulDraftData(prev[RISK_BENEFIT_SCHEMA_KEY])) {
                return prev;
            }

            return {
                ...prev,
                [RISK_BENEFIT_SCHEMA_KEY]: {
                    ...baseDocumentDefaults,
                    ...(hasInitialEvaluationContext
                        ? createRiskBenefitDefaultsFromInitialEvaluationData(
                              currentInitialEvaluationData,
                              initialDocument?.document_id
                          )
                        : {}),
                },
            };
        });
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('schemaKey', RISK_BENEFIT_SCHEMA_KEY);
            return next;
        });
    };

    const handleRequestPositioningImmobilization = () => {
        setHasRequestedPositioningImmobilization(true);
        setExpandedPanels((prev) => ({
            ...prev,
            [POSITIONING_IMMOBILIZATION_SCHEMA_KEY]: true,
        }));
        setPanelModes((prev) => ({
            ...prev,
            [POSITIONING_IMMOBILIZATION_SCHEMA_KEY]: 'edit',
        }));
        setDraftDocumentDataBySchema((prev) => {
            if (
                hasMeaningfulDraftData(
                    prev[POSITIONING_IMMOBILIZATION_SCHEMA_KEY]
                )
            ) {
                return prev;
            }

            return {
                ...prev,
                [POSITIONING_IMMOBILIZATION_SCHEMA_KEY]: {
                    ...baseDocumentDefaults,
                },
            };
        });
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('schemaKey', POSITIONING_IMMOBILIZATION_SCHEMA_KEY);
            return next;
        });
    };

    const handleRequestDevicePreparation = () => {
        setHasRequestedDevicePreparation(true);
        setExpandedPanels((prev) => ({
            ...prev,
            [DEVICE_PREPARATION_SCHEMA_KEY]: true,
        }));
        setPanelModes((prev) => ({
            ...prev,
            [DEVICE_PREPARATION_SCHEMA_KEY]: 'edit',
        }));
        setDraftDocumentDataBySchema((prev) => {
            if (hasMeaningfulDraftData(prev[DEVICE_PREPARATION_SCHEMA_KEY])) {
                return prev;
            }

            return {
                ...prev,
                [DEVICE_PREPARATION_SCHEMA_KEY]: {
                    ...seededDevicePreparationData,
                },
            };
        });
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('schemaKey', DEVICE_PREPARATION_SCHEMA_KEY);
            return next;
        });
    };

    const buildDocumentInitialData = (schemaKey: CaseDocumentSchemaKey) => {
        if (schemaKey === CASE_CONTEXT_SCHEMA_KEY) {
            const initialCaseId =
                effectiveCaseContextValues.caseId ||
                resolveCaseIdFromFormData(formData);
            return {
                caseId: initialCaseId,
                caseToken: initialCaseId,
                exam: effectiveCaseContextValues.exam || '',
                clinicalIndication:
                    effectiveCaseContextValues.clinicalIndication || '',
                scannerId: effectiveCaseContextValues.scannerId || '',
                scanDate: effectiveCaseContextValues.scanDate || '',
                assessmentDate: effectiveCaseContextValues.scanDate || '',
                authors:
                    effectiveCaseContextValues.authors ||
                    formData?.user?.name ||
                    '',
            };
        }

        const baseDefaults = baseDocumentDefaults;

        if (schemaKey === INITIAL_EVALUATION_SCHEMA_KEY) {
            return seededInitialEvaluationData;
        }

        if (schemaKey === DEVICE_PREPARATION_SCHEMA_KEY) {
            return seededDevicePreparationData;
        }

        if (
            schemaKey === RISK_BENEFIT_SCHEMA_KEY &&
            !riskBenefitDocument &&
            !hasRiskBenefitDraft &&
            (scenarioKey === RISK_BENEFIT_ONLY_SCENARIO ||
                requestedSchemaKey === RISK_BENEFIT_SCHEMA_KEY)
        ) {
            return {
                ...baseDefaults,
                ...(hasInitialEvaluationContext
                    ? createRiskBenefitDefaultsFromInitialEvaluationData(
                          currentInitialEvaluationData,
                          initialDocument?.document_id
                      )
                    : {}),
            };
        }

        return baseDefaults;
    };
    const workspacePreviewDocuments: PreviewRenderDocument[] =
        includedSchemaKeys
            .map((schemaKey) => {
                const schema = schemas[schemaKey]?.schema;
                if (!schema) return null;

                const panelDocument = getDocumentBySchemaKey(
                    caseData,
                    schemaKey
                );
                const panelDraftData = draftDocumentDataBySchema[schemaKey];
                const payloadData = mergeDocumentData(
                    buildDocumentInitialData(schemaKey),
                    panelDocument?.data,
                    panelDraftData
                );
                const importedPreviewContext = (
                    schema.contextImports || []
                ).reduce((acc, importRule) => {
                    const sourceSchemaKey =
                        importRule.sourceSchemaKey as CaseDocumentSchemaKey;
                    const sourceDoc = getDocumentBySchemaKey(
                        caseData,
                        sourceSchemaKey
                    );
                    const sourceDraft =
                        draftDocumentDataBySchema[sourceSchemaKey];
                    const sourceData = {
                        ...sourceDoc?.data,
                        ...sourceDraft,
                    };
                    const importedValue = getNestedValue(
                        sourceData,
                        importRule.sourceField
                    );
                    acc[importRule.id] =
                        importedValue !== undefined
                            ? importedValue
                            : importRule.fallback;
                    return acc;
                }, {} as Record<string, any>);

                const previewData = {
                    ...sharedRuntimeCaseContext,
                    ...payloadData,
                    ...importedPreviewContext,
                };

                return {
                    schema,
                    data: previewData,
                    sourceKey: schemaKey,
                    sourceLabel: schema.title,
                };
            })
            .filter(Boolean) as PreviewRenderDocument[];

    const persistWorkspace = async (
        targetStatus: CaseStatus,
        options: { autosave?: boolean; comment?: string } = {}
    ) => {
        const { autosave = false, comment } = options;

        if (!canEditCaseDocuments) {
            if (!autosave) {
                setWorkspaceError(
                    'This case cannot be edited in the workspace.'
                );
            }
            return;
        }

        const resolvedTargetStatus: CaseStatus = isCompletedCase
            ? 'completed'
            : autosave
            ? 'auto-saved'
            : targetStatus;

        if (!includedSchemaKeys.length) {
            if (!autosave) {
                setWorkspaceError(
                    'No available documents to persist for this scenario.'
                );
            }
            return;
        }

        if (!effectiveCaseContextValues.caseId.trim()) {
            if (!autosave) {
                setWorkspaceError(
                    'Case context is invalid. Case ID is required.'
                );
            }
            return;
        }

        const isComplete = resolvedTargetStatus === 'completed';
        if (autosave) {
            setIsAutoSavingWorkspace(true);
        } else if (isComplete) {
            setIsCompletingWorkspace(true);
        } else {
            setIsSavingWorkspace(true);
        }
        setWorkspaceError(null);

        try {
            let currentCaseId = caseData?._id;
            let latestCase = caseData;

            if (isComplete) {
                for (const schemaKey of includedSchemaKeys) {
                    const schemaPayload = schemas[schemaKey];
                    if (!schemaPayload) continue;

                    const existingDocument = getDocumentBySchemaKey(
                        latestCase,
                        schemaKey
                    );
                    const draftData = draftDocumentDataBySchema[schemaKey];
                    const payloadData = mergeDocumentData(
                        buildDocumentInitialData(schemaKey),
                        existingDocument?.data,
                        draftData
                    );
                    const runtimeData = {
                        ...(runtimeContextBySchema[schemaKey] || {}),
                        ...payloadData,
                    };
                    const validationResult = validateRequiredFields(
                        schemaPayload.schema,
                        payloadData,
                        (condition?: string) =>
                            evaluateExpressionWithContext(
                                condition,
                                runtimeData
                            )
                    );

                    if (!validationResult.isValid) {
                        const missingText = validationResult.missingFields
                            .map((field) => field.title)
                            .join(', ');
                        throw new Error(
                            `Cannot complete ${schemaPayload.schema.title}. Missing required fields: ${missingText}`
                        );
                    }
                }
            }

            for (const schemaKey of includedSchemaKeys) {
                const schemaPayload = schemas[schemaKey];
                if (!schemaPayload) {
                    throw new Error(`Schema missing for ${schemaKey}`);
                }

                const existingDocument = getDocumentBySchemaKey(
                    latestCase,
                    schemaKey
                );
                const draftData = draftDocumentDataBySchema[schemaKey];
                const payloadData = mergeDocumentData(
                    buildDocumentInitialData(schemaKey),
                    existingDocument?.data,
                    draftData
                );

                const savedCase = await saveReport(
                    payloadData,
                    currentCaseId,
                    payloadData.title || `${schemaPayload.schema.title}`,
                    resolvedTargetStatus,
                    schemaPayload.key,
                    schemaPayload.version,
                    !autosave && schemaKey === CASE_CONTEXT_SCHEMA_KEY
                        ? comment
                        : undefined
                );

                latestCase = savedCase;
                currentCaseId = savedCase._id;
            }

            if (latestCase) {
                // When auto-save creates documents for the first time,
                // panels that were rendering as editors solely because they
                // had no saved document (!panelDocument) would flip to
                // ReadView once caseData is updated — their panelModes
                // entry is still 'view' because the user never interacted
                // with them directly.  Promote those panels to explicit
                // 'edit' mode before updating caseData so they stay
                // editable.
                if (autosave) {
                    setPanelModes((prev) => {
                        const next = { ...prev };
                        let changed = false;
                        for (const schemaKey of includedSchemaKeys) {
                            if (next[schemaKey] !== 'edit') {
                                const hadDocument = !!getDocumentBySchemaKey(
                                    caseData,
                                    schemaKey
                                );
                                if (!hadDocument) {
                                    next[schemaKey] = 'edit';
                                    changed = true;
                                }
                            }
                        }
                        return changed ? next : prev;
                    });
                }
                setCaseData(latestCase);
            }

            // For auto-save: keep the user's drafts, edit mode, and URL state
            // intact so typing is not interrupted.
            if (!autosave) {
                setDraftDocumentDataBySchema({});
                setPanelModes((prev) => {
                    const next = { ...prev };
                    includedSchemaKeys.forEach((schemaKey) => {
                        next[schemaKey] = 'view';
                    });
                    return next;
                });
                setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('schemaKey');
                    return next;
                });
            }
        } catch (persistError: any) {
            if (autosave) {
                throw persistError;
            }
            const message =
                persistError?.response?.data?.message ||
                persistError?.message ||
                `Failed to ${isComplete ? 'complete' : 'save'} workspace.`;
            setWorkspaceError(message);
        } finally {
            if (autosave) {
                setIsAutoSavingWorkspace(false);
            } else if (isComplete) {
                setIsCompletingWorkspace(false);
            } else {
                setIsSavingWorkspace(false);
            }
        }
    };

    const handlePreviewWorkspace = () => {
        trackClick(EVENT_KEYS.clickExport, { previewSource: 'cpt' });
        setWorkspaceError(null);
        setPreviewOpen(true);
    };

    const autoSaveEnabled =
        canEditCaseDocuments &&
        !isCompletedCase &&
        !isSavingWorkspace &&
        !isCompletingWorkspace &&
        hasUserEdited &&
        Object.values(draftDocumentDataBySchema).some(hasMeaningfulDraftData);

    const { autoSaveStatus, lastSavedAt, cancelPendingAutoSave } = useAutoSave({
        save: async () => {
            try {
                await persistWorkspace('auto-saved', { autosave: true });
                return { success: true };
            } catch (err: any) {
                return {
                    success: false,
                    error: err?.message ?? 'Auto-save failed',
                };
            }
        },
        data: draftDocumentDataBySchema,
        enabled: autoSaveEnabled,
        debounceMs: 2000,
        skipAsBaselineRef: skipAutoSaveAsBaselineRef,
    });

    if (isLoading) {
        return (
            <div className={pageShellClassName}>
                <Card>
                    <CardContent className="flex items-center justify-center py-16">
                        <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error || (!caseData && !isNewCaseMode)) {
        return (
            <div className={pageShellClassName}>
                <div className="flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm">
                        <Link to="/manage-reports">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Cases
                        </Link>
                    </Button>
                </div>
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                        {error || 'Case not found.'}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    if (isLegacyCase) {
        return (
            <div className={pageShellClassName}>
                <div className="flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm">
                        <Link to="/manage-reports">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Cases
                        </Link>
                    </Button>
                </div>
                <Card>
                    <CardHeader className="border-b pb-4">
                        <CardTitle className="text-xl tracking-tight">
                            Unsupported Case Version
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 pt-5 text-sm text-muted-foreground">
                        <p>
                            This case was created before the modular Case
                            Workspace cutover and cannot be edited here.
                        </p>
                        <p>
                            Legacy case shapes are intentionally not supported
                            in this workspace.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const displayedCaseId = effectiveCaseContextValues.caseId || 'New Case';
    const displayedExam = effectiveCaseContextValues.exam || 'Exam not set';
    const displayedClinicalIndication =
        effectiveCaseContextValues.clinicalIndication ||
        'Clinical indication not set';
    const displayedCodes = effectiveApplicableCodes;
    const showRiskBenefitCta =
        scenarioKey !== RISK_BENEFIT_ONLY_SCENARIO &&
        canEditCaseDocuments &&
        !riskBenefitDocument &&
        !hasRiskBenefitDraft &&
        !isRiskBenefitRequested &&
        (isCompletedCase || !!currentFeasibilitySelection);
    const showPositioningImmobilizationCta =
        canEditCaseDocuments &&
        !positioningImmobilizationDocument &&
        !hasPositioningImmobilizationDraft &&
        !isPositioningImmobilizationRequested;
    const showDevicePreparationCta =
        scenarioKey !== DEVICE_PREPARATION_ONLY_SCENARIO &&
        canEditCaseDocuments &&
        !devicePreparationDocument &&
        !hasDevicePreparationDraft &&
        !isDevicePreparationRequested;

    const hasEditLog = !!caseData?.editLog && caseData.editLog.length > 0;

    return (
        <>
            <div className={pageShellClassName}>
                <div className="flex items-center justify-between">
                    <Button asChild variant="ghost" size="sm">
                        <Link to="/manage-reports">
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Cases
                        </Link>
                    </Button>
                </div>

                <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
                    <div className="order-2 min-w-0 flex-1 space-y-6 lg:order-1">
                        <Card>
                            <CardHeader className="space-y-5 border-b pb-5">
                                <div className="flex flex-wrap items-center gap-2">
                                    <CardTitle className="text-[clamp(1.75rem,2vw,2.1rem)] tracking-tight">
                                        Case
                                    </CardTitle>
                                    <Badge variant="outline">
                                        {displayedCaseId}
                                    </Badge>
                                    {activeImplantRef && (
                                        <Button
                                            asChild
                                            variant="ghost"
                                            size="sm"
                                        >
                                            <Link
                                                to={buildActiveImplantBacklinkPath(
                                                    activeImplantRef,
                                                    caseData?._id
                                                )}
                                            >
                                                <ExternalLink className="mr-2 h-4 w-4" />
                                                View{' '}
                                                {activeImplantRef.systemName ||
                                                    'active implant'}{' '}
                                                config
                                            </Link>
                                        </Button>
                                    )}
                                </div>
                                <CardDescription className="space-y-1 text-sm">
                                    <div>{displayedExam}</div>
                                    <div>{displayedClinicalIndication}</div>
                                </CardDescription>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            (displayedCodes['76014'] ?? 0) >
                                                0 &&
                                                'text-[#6AA2FF] border-[#6AA2FF]'
                                        )}
                                    >
                                        76014: {displayedCodes['76014']}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            (displayedCodes['76015'] ?? 0) >
                                                0 &&
                                                'text-[#6AA2FF] border-[#6AA2FF]'
                                        )}
                                    >
                                        76015: {displayedCodes['76015']}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            (displayedCodes['76016'] ?? 0) >
                                                0 &&
                                                'text-[#6AA2FF] border-[#6AA2FF]'
                                        )}
                                    >
                                        76016: {displayedCodes['76016']}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            (displayedCodes['76018'] ?? 0) >
                                                0 &&
                                                'text-[#6AA2FF] border-[#6AA2FF]'
                                        )}
                                    >
                                        76018: {displayedCodes['76018']}
                                    </Badge>
                                    <Badge
                                        variant="outline"
                                        className={cn(
                                            (displayedCodes['76019'] ?? 0) >
                                                0 &&
                                                'text-[#6AA2FF] border-[#6AA2FF]'
                                        )}
                                    >
                                        76019: {displayedCodes['76019']}
                                    </Badge>
                                </div>
                            </CardHeader>
                        </Card>

                        {scenarioConfig.documentFlows.map((flow) => {
                            const panelKey = flow.schemaKey;
                            const panelSchema = schemas[panelKey];
                            const panelDocument = getDocumentBySchemaKey(
                                caseData,
                                panelKey
                            );
                            const panelDraftData =
                                draftDocumentDataBySchema[panelKey];
                            const isExpanded = !!expandedPanels[panelKey];
                            const mode = panelModes[panelKey] || 'view';
                            const isVisible = !!panelVisibility[panelKey];
                            const shouldRenderEditMode =
                                canEditCaseDocuments &&
                                (!panelDocument || mode === 'edit') &&
                                (!isCompletedCase || completedEditConfirmed);

                            if (!isVisible) {
                                return null;
                            }

                            return (
                                <Card
                                    key={panelKey}
                                    data-panel-key={panelKey}
                                    className={resolveSchemaCardClassName(
                                        panelSchema?.schema
                                    )}
                                >
                                    <CardHeader className="border-b pb-5">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                            <div className="space-y-1">
                                                <CardTitle className="text-xl tracking-tight">
                                                    {panelSchema?.schema
                                                        ?.title || panelKey}
                                                </CardTitle>
                                                <p className="text-sm text-muted-foreground">
                                                    {panelSchema?.schema
                                                        ?.description ||
                                                        'Case document'}
                                                </p>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() =>
                                                        togglePanel(panelKey)
                                                    }
                                                >
                                                    {isExpanded ? (
                                                        <ChevronDown className="h-4 w-4" />
                                                    ) : (
                                                        <ChevronRight className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>

                                    {isExpanded && (
                                        <CardContent className="space-y-4 pt-5">
                                            {shouldRenderEditMode ? (
                                                <div className="pt-2">
                                                    {panelSchema ? (
                                                        <CaseDocumentEditor
                                                            schema={
                                                                panelSchema.schema
                                                            }
                                                            schemaKey={
                                                                panelSchema.key
                                                            }
                                                            schemaVersion={
                                                                panelSchema.version
                                                            }
                                                            dataSources={
                                                                dataSources
                                                            }
                                                            applicableCodes={
                                                                caseData?.cptEligibleCodes
                                                            }
                                                            initialData={mergeDocumentData(
                                                                buildDocumentInitialData(
                                                                    panelKey
                                                                ),
                                                                panelDocument?.data,
                                                                panelDraftData
                                                            )}
                                                            externalContextData={
                                                                runtimeContextBySchema[
                                                                    panelKey
                                                                ] || {}
                                                            }
                                                            caseRecordId={
                                                                caseData?._id
                                                            }
                                                            reportData={
                                                                panelDocument
                                                                    ? {
                                                                          data: panelDocument.data,
                                                                          status: panelDocument.status,
                                                                      }
                                                                    : undefined
                                                            }
                                                            onDataChange={(
                                                                nextData
                                                            ) =>
                                                                handlePanelDataChange(
                                                                    panelKey,
                                                                    nextData
                                                                )
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="flex items-center justify-center py-6">
                                                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                                        </div>
                                                    )}
                                                </div>
                                            ) : panelDocument && panelSchema ? (
                                                <CaseDocumentReadView
                                                    schema={panelSchema.schema}
                                                    data={{
                                                        ...sharedRuntimeCaseContext,
                                                        ...(panelDocument.data ||
                                                            {}),
                                                    }}
                                                    dataSources={dataSources}
                                                    readOnlyFieldIds={[]}
                                                    meta={{
                                                        caseId: displayedCaseId,
                                                        status: panelDocument.status,
                                                        createdAt:
                                                            caseData?.updatedAt ||
                                                            new Date().toISOString(),
                                                    }}
                                                    canEdit={
                                                        canEditCaseDocuments
                                                    }
                                                    onEdit={() =>
                                                        setPanelMode(
                                                            panelKey,
                                                            'edit'
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <Card>
                                                    <CardContent className="py-6 text-sm text-muted-foreground">
                                                        No document exists for
                                                        this case.
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </CardContent>
                                    )}
                                </Card>
                            );
                        })}

                        {showRiskBenefitCta && (
                            <Alert>
                                <FileText className="h-4 w-4" />
                                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                    <div className="space-y-1 ">
                                        <AlertTitle className="mt-1">
                                            Risk-Benefit Analysis is available
                                        </AlertTitle>
                                        <AlertDescription>
                                            Add the physician/QHP risk-benefit
                                            analysis for this case.
                                        </AlertDescription>
                                    </div>
                                    <div>
                                        <Button
                                            onClick={handleRequestRiskBenefit}
                                        >
                                            Add Risk-Benefit Analysis
                                        </Button>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {showPositioningImmobilizationCta && (
                            <Alert>
                                <FileText className="h-4 w-4" />
                                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                    <div className="space-y-1 ">
                                        <AlertTitle className="mt-1">
                                            Positioning / Immobilization is
                                            available
                                        </AlertTitle>
                                        <AlertDescription>
                                            Document implant-directed
                                            positioning or immobilization steps
                                            for this case.
                                        </AlertDescription>
                                    </div>
                                    <div>
                                        <Button
                                            onClick={
                                                handleRequestPositioningImmobilization
                                            }
                                        >
                                            Add Positioning / Immobilization
                                        </Button>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {showDevicePreparationCta && (
                            <Alert>
                                <FileText className="h-4 w-4" />
                                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                    <div className="space-y-1 ">
                                        <AlertTitle className="mt-1">
                                            Device Preparation (76018) is
                                            available
                                        </AlertTitle>
                                        <AlertDescription>
                                            Document implanted-device
                                            preparation performed on the day of
                                            the MRI exam.
                                        </AlertDescription>
                                    </div>
                                    <div>
                                        <Button
                                            onClick={
                                                handleRequestDevicePreparation
                                            }
                                        >
                                            Add Device Preparation
                                        </Button>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {workspaceError && (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    {workspaceError}
                                </AlertDescription>
                            </Alert>
                        )}

                        <CaseWorkspaceFooter
                            schema={footerSchema}
                            onPreview={handlePreviewWorkspace}
                            onSaveDraft={() => {
                                cancelPendingAutoSave();
                                setSaveCommentDialog({
                                    open: true,
                                    targetStatus: 'draft',
                                });
                            }}
                            onComplete={() => {
                                cancelPendingAutoSave();
                                setSaveCommentDialog({
                                    open: true,
                                    targetStatus: 'completed',
                                });
                            }}
                            isSaving={isSavingWorkspace}
                            isCompleting={isCompletingWorkspace}
                            isPreviewing={false}
                            disabled={!includedSchemaKeys.length}
                            readOnly={!canEditCaseDocuments}
                            hideSaveDraft={isCompletedCase}
                        />

                        <CaseSaveCommentDialog
                            open={saveCommentDialog.open}
                            isComplete={
                                saveCommentDialog.targetStatus === 'completed'
                            }
                            commentRequired={isCompletedCase}
                            onConfirm={(comment) => {
                                setSaveCommentDialog((prev) => ({
                                    ...prev,
                                    open: false,
                                }));
                                persistWorkspace(
                                    saveCommentDialog.targetStatus,
                                    {
                                        comment,
                                    }
                                );
                            }}
                            onCancel={() =>
                                setSaveCommentDialog((prev) => ({
                                    ...prev,
                                    open: false,
                                }))
                            }
                        />

                        <EditCompletedReportDialog
                            open={pendingEditPanelKey !== null}
                            onConfirm={() => {
                                setCompletedEditConfirmed(true);
                                if (pendingEditPanelKey) {
                                    commitPanelEdit(pendingEditPanelKey);
                                }
                                setPendingEditPanelKey(null);
                            }}
                            onCancel={() => setPendingEditPanelKey(null)}
                        />
                    </div>

                    <div className="order-1 w-full shrink-0 lg:order-2 lg:w-56">
                        <div className="space-y-4 lg:sticky lg:top-8">
                            <div className="min-h-[1rem]">
                                <AutoSaveIndicator
                                    status={autoSaveStatus}
                                    lastSavedAt={lastSavedAt}
                                />
                            </div>
                            {hasEditLog && (
                                <EditLog
                                    editLog={caseData!.editLog!}
                                    hidePreviewOnMobile
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
                    <DialogHeader className="shrink-0 border-b px-6 pb-4 pt-6 pr-14">
                        <DialogTitle>Case Workspace Preview</DialogTitle>
                    </DialogHeader>
                    <CptReportPreview
                        className="min-h-0 flex-1"
                        documents={workspacePreviewDocuments}
                        applicableCodes={effectiveApplicableCodes}
                        dataSources={dataSources}
                        caseId={displayedCaseId}
                        onClose={() => setPreviewOpen(false)}
                    />
                </DialogContent>
            </Dialog>

            <NavigationGuard
                when={isCompletedCase && hasUserEdited}
                title="Unsaved Changes"
                description="You have unsaved changes to this completed report. Leaving without saving will discard your edits."
                confirmLabel="Leave Page"
                cancelLabel="Stay"
            />
        </>
    );
};

export default CaseWorkspacePage;
